import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { hasPermission } from '@application/helpers/permission-helper';
import { Icon } from '@iconify/react';
import { Avatar, Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import { StyledMenu } from '@ui/components/common/Menu';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete } from 'react-iconly';
import { useSelector } from 'react-redux';
import { useFamilyMemberCardStyles } from './useFamilyMemberCardStyles';
import { isZeroDate } from '@application/utilities/date-utils';
const FamilyMemberCard = ({ member, handleEditMember, handleDeleteMember }) => {
    const styles = useFamilyMemberCardStyles();
    const theme = useTheme();
    const { t } = useTranslation();
    const [anchorFamilyMemberEl, setAnchorFamilyMemberEl] = useState(null);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canDeletePeople = hasPermission(loggedInUserPermissions, 'Permissions.VdkPeople.Delete');
    const canUpdatePeople = hasPermission(loggedInUserPermissions, 'Permissions.VdkPeople.Update');
    const handleOptionsClick = (event) => {
        setAnchorFamilyMemberEl(event.currentTarget);
    };
    const handleDeleteClick = () => {
        setAnchorFamilyMemberEl(null);
        setDeleteConfirmationOpen(true);
    };
    const handleConfirmDelete = () => {
        handleDeleteMember(member.id);
        setDeleteConfirmationOpen(false);
    };
    const familyMembersMenu = [
        {
            text: t('tasks.edit'),
            group: true,
            to: () => handleEditMember(member),
            icon: _jsx(Icon, { icon: 'eva:edit-outline', fontSize: 20 }),
            disabled: !canUpdatePeople
        },
        {
            text: t('tasks.delete'),
            group: true,
            color: theme.palette.error.light,
            to: handleDeleteClick,
            icon: _jsx(Delete, { style: { width: 20, height: 20 } }),
            disabled: !canDeletePeople
        }
    ];
    const getAgeFromDateOfBirth = (dateOfBirth) => {
        const birthDate = typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };
    return (_jsxs(Box, { sx: styles.container, children: [_jsx(Box, { sx: styles.optionsButton, children: _jsx(IconButton, { size: 'small', onClick: handleOptionsClick, sx: styles.optionsIcon, children: _jsx(Icon, { icon: 'eva:more-horizontal-fill', fontSize: 20, color: theme.palette.text.primary }) }) }), _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', children: [_jsx(Avatar, { src: member.fileUrl || undefined, alt: member.name, sx: styles.avatar, children: member.name.charAt(0).toUpperCase() }), _jsxs(Box, { sx: styles.memberInfo, children: [_jsx(Typography, { variant: 'subtitle2', color: 'text.primary', children: member.name }), _jsxs(Box, { display: 'flex', flexDirection: 'column', children: [_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: member.relationship }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: member.dateOfBirth && !isZeroDate(String(member.dateOfBirth))
                                            ? `${getAgeFromDateOfBirth(member.dateOfBirth)} ${t('common.yearsOld')}`
                                            : '' })] })] })] }), anchorFamilyMemberEl && (_jsx(StyledMenu, { anchorEl: anchorFamilyMemberEl, open: Boolean(anchorFamilyMemberEl), onClose: () => setAnchorFamilyMemberEl(null), actions: familyMembersMenu })), _jsx(ConfirmationDialog, { open: deleteConfirmationOpen, onClose: () => setDeleteConfirmationOpen(false), title: t('common.deleteConfirmation'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => setDeleteConfirmationOpen(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', size: 'large', fullWidth: true, onClick: handleConfirmDelete, children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('people.deleteFamilyMemberConfirmation', {
                            name: member.name
                        }) }) }) })] }));
};
export default FamilyMemberCard;
